import type { CleanupGroup } from '@/bindings/CleanupGroup'
import type { CleanupPreview } from '@/bindings/CleanupPreview'
import type { CleanupRequest } from '@/bindings/CleanupRequest'
import type { CleanupResult } from '@/bindings/CleanupResult'
import type { TableDeleted } from '@/bindings/TableDeleted'
import { request } from '@/lib/api'
import type { MessageKey } from '@/lib/i18n/messages/en'

/** Page order; each group is one card with its own cutoff. */
export const CLEANUP_GROUPS: readonly CleanupGroup[] = ['orders', 'system_log', 'notifications', 'verification_codes', 'audit_log', 'feedback']

export function previewCleanup(body: CleanupRequest) {
  return request<CleanupPreview>('/platform/cleanup/preview', { method: 'POST', body })
}

export function runCleanupBatch(body: CleanupRequest) {
  return request<CleanupResult>('/platform/cleanup', { method: 'POST', body })
}

/**
 * Keeps calling until the server says `done`, reporting running totals after
 * every call so the page can show progress instead of a silent wait.
 */
export async function runCleanup(body: CleanupRequest, onProgress: (totals: TableDeleted[]) => void): Promise<TableDeleted[]> {
  let totals: TableDeleted[] = []
  for (;;) {
    const result = await runCleanupBatch(body)
    totals = result.tables.map((table, i) => ({ table: table.table, rows: table.rows + (totals[i]?.rows ?? 0) }))
    onProgress(totals)
    if (result.done) return totals
  }
}

/** A `date` value is a local calendar day; the cutoff is its start, so rows before that day go. */
export function toCutoff(local: string): string | undefined {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(local)) return undefined
  const date = new Date(`${local}T00:00`)
  if (Number.isNaN(date.getTime()) || date.getTime() >= Date.now()) return undefined
  return date.toISOString()
}

const UNITS = ['B', 'KB', 'MB', 'GB', 'TB'] as const

/** Approximate on purpose: the server's number is an estimate already. */
export function formatBytes(bytes: number): string {
  let value = bytes
  let unit = 0
  while (value >= 1024 && unit < UNITS.length - 1) {
    value /= 1024
    unit += 1
  }
  return `${unit === 0 ? value : value.toFixed(1)} ${UNITS[unit]}`
}

const TABLE_KEYS: Record<string, MessageKey> = {
  orders: 'cleanup.table.orders',
  order_lines: 'cleanup.table.order_lines',
  dining_sessions: 'cleanup.table.dining_sessions',
  order_counters: 'cleanup.table.order_counters',
  system_log: 'cleanup.table.system_log',
  notifications: 'cleanup.table.notifications',
  verification_codes: 'cleanup.table.verification_codes',
  audit_log: 'cleanup.table.audit_log',
  feedback: 'cleanup.table.feedback',
  // The rest belong to the restaurant delete (K4), which previews the same way.
  branches: 'cleanup.table.branches',
  restaurant_roles: 'cleanup.table.restaurant_roles',
  role_branches: 'cleanup.table.role_branches',
  menu_categories: 'cleanup.table.menu_categories',
  menu_items: 'cleanup.table.menu_items',
  branch_menu_items: 'cleanup.table.branch_menu_items',
  dining_tables: 'cleanup.table.dining_tables',
  images: 'cleanup.table.images',
  restaurants: 'cleanup.table.restaurants',
}

/** A table the page has no name for shows its raw name rather than nothing. */
export function tableKey(table: string): MessageKey | undefined {
  return TABLE_KEYS[table]
}
